import { Button } from "@mui/material";
import styled from "@emotion/styled";
import { CrossButton } from "../CrossButton/CrossButton";

// Popup is intentionally not scrollable: when there are too many packages we cap visible
// cards and link to /uploads/in-progress for the full list. The previous nested-scroll layout
// (popup scroll + per-card file-list scroll) was confusing once more than one package was in
// flight; the page-based overflow is the new home for "I have lots of uploads at once".
export const UploadProgressPanel = styled.div`
    background-color: #29384d8c;
    backdrop-filter: blur(10px);
    padding: 8px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 420px;
    max-width: calc(100vw - 24px);
    border: 1px solid rgba(255, 255, 255, 0.08);

    @media screen and (max-width: 1008px) {
        max-width: calc(100vw - 24px);
        padding: 12px;
    }
`;

export const UploadProgressCard = styled.div`
    background-color: #29384d8c;
    backdrop-filter: blur(10px);
    padding: 12px 14px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    position: relative;
`;

export const UploadProgressCardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
`;

export const UploadProgressCardRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    width: 100%;
`;

export const UploadProgressIconWrapper = styled.span`
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    color: ${({ status, theme }) =>
            (status === "succeeded" && theme.palette.success.main) ||
            (status === "failed" && theme.palette.error.main) ||
            (status === "canceled" && theme.palette.grey[500]) ||
            // Paused: warm accent so a glance at the icon alone tells the
            // user the upload is intentionally halted (rather than dead or
            // stuck) — see bug 2026-08-04 UX pass.
            (status === "PAUSED" && theme.palette.warning.main) ||
            theme.palette.primary.main};
`;

export const UploadProgressInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const UploadProgressTitle = styled.div`
    font-size: 14px;
    color: #f7fafa;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const UploadProgressNumData = styled.div`
    font-size: 12px;
    color: #f7fafaa6;
    display: flex;
    justify-content: space-between;
    gap: 8px;
`;

export const UploadProgressMessage = styled.div`
  font-size: 13px;
  color: #f7fafaa6;
  line-height: 18px;
`;

export const UploadProgressNetworkStatus = styled.div`
  font-size: 12px;
  margin-top: 4px;
  color: ${({ severity, theme }) =>
    severity === "danger"
      ? theme.palette.error.light
      : theme.palette.warning.light};
`;

export const UploadProgressButton = styled(Button)`
    padding: 6px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: #fff;
    text-transform: none;

    &:hover {
        background-color: ${({ theme }) => theme.palette.primary.light};
    }
`;

export const UploadProgressSecondButton = styled(Button)`
    padding: 6px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    background-color: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.92);
    text-transform: none;
    border: 1px solid rgba(255, 255, 255, 0.16);

    &:hover {
        background-color: rgba(255, 255, 255, 0.18);
        border-color: rgba(255, 255, 255, 0.28);
    }
`;


export const UploadProgressCardActions = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
    width: 100%;
    margin-top: 4px;
`;

export const UploadAbsCrossButton = styled(CrossButton)`
    flex-shrink: 0;
    opacity: 0.9;
`;

export const UploadProgressSecondaryInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #f7fafabf;
    margin-top: 2px;
`;

export const UploadProgressSpeedDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${({ theme }) => theme.palette.primary.main};
    box-shadow: 0 0 0 rgba(93, 98, 182, 0.45);
    animation: uploadSpeedPulse 1.6s ease-in-out infinite;

    @keyframes uploadSpeedPulse {
        0% {
            transform: scale(0.9);
            opacity: 0.75;
            box-shadow: 0 0 0 0 rgba(93, 98, 182, 0.35);
        }
        70% {
            transform: scale(1);
            opacity: 1;
            box-shadow: 0 0 0 8px rgba(93, 98, 182, 0);
        }
        100% {
            transform: scale(0.9);
            opacity: 0.75;
            box-shadow: 0 0 0 0 rgba(93, 98, 182, 0);
        }
    }
`;

export const UploadProgressSpeedLabel = styled.span`
    color: #f7fafaa6;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
`;

export const UploadProgressSpeedValue = styled.span`
    color: #f7fafa;
    font-size: 12px;
    font-weight: 700;
`;